
import { useState, useRef, useEffect } from 'react'
import {
  Outlet,
  NavLink,
  useNavigate,
  useLocation,
} from 'react-router-dom'

import {
  LayoutDashboard,
  FileText,
  Edit3,
  Settings,
  ChevronDown,
  LogOut,
  HelpCircle,
  UtensilsCrossed,
  Star,
  Receipt,
   ShoppingBag,
  UserCog,
  Nfc,
  Menu,
  X,
  User as UserIcon,
  QrCode,
} from 'lucide-react'

import Topbar from './Topbar'
import Avatar from '../ui/Avatar'
import { cn } from '../../lib/utils'
import { useAuthStore } from '../../stores/authStore'
import api from '../../lib/axios'

/* ============================================
   ADMIN MENU
   ============================================ */

const adminMenu = [
  {
    to: '/app/overview',
    icon: LayoutDashboard,
    label: "Vue d'ensemble",
  },
     { to: '/app/admin/products', icon: ShoppingBag, label: 'Produits (admin)' },
  {
    to: '/app/admin/orders',
    icon: Receipt,
    label: 'Commandes clients (admin)',
  },
  {
    to: '/app/admin/subscriptions',
    icon: UserCog,
    label: 'Clients & Abonnements',
  },
  {
    to: '/app/admin/nfc-tools',
    icon: Nfc,
    label: 'NFC Tools',
  },
  {
    to: '/app/profile',
    icon: UserIcon,
    label: 'Mon profil',
  },
  {
    to: '/app/pages',
    icon: FileText,
    label: 'Mes Pages',
  },
  {
    to: '/app/pages/new',
    icon: Edit3,
    label: 'Éditeur de Page',
  },
  {
    to: '/app/menus/new',
    icon: UtensilsCrossed,
    label: 'Menu NFC',
  },
  {
    to: '/app/reviews/new',
    icon: Star,
    label: 'Avis Google',
  },
  {
    to: '/app/qrs',
    icon: QrCode,
    label: 'Générateur QR',
  },
 
  {
    to: '/app/settings',
    icon: Settings,
    label: 'Paramètres & Support',
  },
]

/* ============================================
   CLIENT MENU
   ============================================ */

const getClientMenu = (hasMenu) => [
  {
    to: '/app/pages',
    icon: FileText,
    label: 'Mes Pages',
  },
  ...(hasMenu
    ? [
        {
          to: '/app/menu-orders',
          icon: Receipt,
          label: 'Commandes du menu',
        },
      ]
    : []),
  {
    to: '/app/profile',
    icon: UserIcon,
    label: 'Mon profil',
  },
  {
    to: '/app/settings',
    icon: Settings,
    label: 'Paramètres',
  },
]

/* ============================================
   EXTRACTION DES PAGES
   ============================================ */

function extractPages(data) {
  if (Array.isArray(data)) return data
  if (Array.isArray(data?.data)) return data.data
  if (Array.isArray(data?.data?.data)) return data.data.data
  if (Array.isArray(data?.pages)) return data.pages

  return []
}

function userHasMenu(pages) {
  return pages.some((page) => {
    const type = String(
      page?.type || page?.page_type || ''
    ).toLowerCase().trim()

    return type === 'menu'
  })
}

/* ============================================
   LINK STYLE
   ============================================ */

const linkClass = ({ isActive }) =>
  cn(
    'flex min-w-0 items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all select-none',
    isActive
      ? 'bg-navy text-white shadow-sm'
      : 'text-gray-600 hover:bg-graysoft hover:text-navy'
  )

/* ============================================
   SIDEBAR CONTENT
   ============================================ */

function SidebarContent({ mobileOpen, onClose }) {
  const navigate = useNavigate()

  const user = useAuthStore((state) => state.user)
  const logout = useAuthStore((state) => state.logout)
  const isAdmin = useAuthStore((state) => state.isAdmin())

  const [menuOpen, setMenuOpen] = useState(false)
  const [hasMenu, setHasMenu] = useState(false)
  const [checkingMenu, setCheckingMenu] = useState(!isAdmin)

  const menuRef = useRef(null)

  // Vérifier si le client possède un Menu NFC
  useEffect(() => {
    let active = true

    const checkMenu = async () => {
      if (isAdmin || !user) {
        setCheckingMenu(false)
        return
      }

      setCheckingMenu(true)

      try {
        const { data } = await api.get('/pages')
        const pages = extractPages(data)

        if (active) {
          setHasMenu(userHasMenu(pages))
        }
      } catch (error) {
        console.error('Erreur vérification Menu NFC:', error)

        if (active) {
          setHasMenu(false)
        }
      } finally {
        if (active) {
          setCheckingMenu(false)
        }
      }
    }

    checkMenu()

    return () => {
      active = false
    }
  }, [isAdmin, user])

  // Fermer dropdown en cliquant à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target)
      ) {
        setMenuOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  // Fermer dropdown sur mobile
  useEffect(() => {
    if (mobileOpen) {
      setMenuOpen(false)
    }
  }, [mobileOpen])

  // Déconnexion
  const handleLogout = async () => {
    if (!window.confirm('Voulez-vous vraiment vous déconnecter ?')) {
      return
    }

    await logout()
    navigate('/login')
  }

  // Navigation dropdown
  const go = (path) => {
    setMenuOpen(false)
    onClose?.()
    navigate(path)
  }

  const items = isAdmin
    ? adminMenu
    : getClientMenu(hasMenu)

  return (
    <>
      {/* OVERLAY MOBILE */}
      {mobileOpen && (
        <button
          type="button"
          aria-label="Fermer le menu"
          onClick={onClose}
          className="fixed inset-0 z-30 bg-black/40 lg:hidden"
        />
      )}

      <aside
        className={cn(
          'fixed left-0 top-0 z-40 flex h-screen h-[100dvh] w-64 max-w-[85vw] flex-col overflow-hidden border-r border-gray-100 bg-white',
          'transition-transform duration-300 ease-in-out',
          'lg:translate-x-0 lg:z-20',
          mobileOpen
            ? 'translate-x-0 shadow-2xl'
            : '-translate-x-full'
        )}
        aria-label="Navigation principale"
      >
        {/* WORKSPACE */}
        <div
          className="relative flex-shrink-0 border-b border-gray-100 p-4"
          ref={menuRef}
        >
          <div className="flex min-w-0 items-center gap-2">
            <button
              type="button"
              onClick={() => setMenuOpen((value) => !value)}
              className="flex min-w-0 flex-1 items-center gap-3 rounded-xl p-2 transition-colors hover:bg-graysoft"
            >
              <Avatar
                name={user?.name || 'Mon compte'}
                size="md"
              />

              <div className="min-w-0 flex-1 text-left">
                <p className="truncate text-sm font-semibold text-navy">
                  {user?.name || 'Mon compte'}
                </p>

                <p className="truncate text-xs text-gray-500">
                  {user?.email || ''}
                </p>
              </div>

              <ChevronDown
                className={cn(
                  'h-4 w-4 flex-shrink-0 text-gray-400 transition-transform',
                  menuOpen && 'rotate-180'
                )}
              />
            </button>

            <button
              type="button"
              onClick={onClose}
              className="rounded-xl p-2 text-gray-500 transition-colors hover:bg-graysoft lg:hidden"
              aria-label="Fermer le menu"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* USER DROPDOWN */}
          {menuOpen && (
            <div className="absolute left-4 right-4 top-full z-50 mt-1 overflow-hidden rounded-xl border border-gray-100 bg-white shadow-lg">
              <div className="border-b border-gray-100 p-3">
                <div className="flex min-w-0 items-center gap-3">
                  <Avatar
                    name={user?.name || 'Mon compte'}
                    size="md"
                  />

                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-navy">
                      {user?.name || 'Mon compte'}
                    </p>

                    <p className="truncate text-xs text-gray-500">
                      {user?.email || ''}
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-1">
                <button
                  type="button"
                  onClick={() => go('/app/profile')}
                  className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-gray-700 hover:bg-graysoft"
                >
                  <UserIcon className="h-4 w-4 text-gray-500" />
                  Mon profil
                </button>

                <button
                  type="button"
                  onClick={() => go('/app/settings')}
                  className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-gray-700 hover:bg-graysoft"
                >
                  <Settings className="h-4 w-4 text-gray-500" />
                  Paramètres
                </button>

                <button
                  type="button"
                  onClick={() => go('/app/support')}
                  className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-gray-700 hover:bg-graysoft"
                >
                  <HelpCircle className="h-4 w-4 text-gray-500" />
                  Aide & support
                </button>
              </div>

              <div className="border-t border-gray-100 p-1">
                <button
                  type="button"
                  onClick={handleLogout}
                  className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                >
                  <LogOut className="h-4 w-4" />
                  Se déconnecter
                </button>
              </div>
            </div>
          )}
        </div>

        {/* NAVIGATION */}
        <nav className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-contain p-3">
          {!isAdmin && checkingMenu ? (
            <div className="flex items-center justify-center py-6">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-teal/30 border-t-teal" />
            </div>
          ) : (
            <div className="space-y-1">
              {items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/app/pages'}
                  className={linkClass}
                  onClick={() => onClose?.()}
                >
                  <item.icon className="h-5 w-5 flex-shrink-0" />

                  <span className="min-w-0 flex-1 truncate">
                    {item.label}
                  </span>
                </NavLink>
              ))}
            </div>
          )}
        </nav>
      </aside>
    </>
  )
}

/* ============================================
   DASHBOARD LAYOUT
   ============================================ */

export default function DashboardLayout() {
  const [mobileOpen, setMobileOpen] = useState(false)
  const location = useLocation()

  const isEditorPage =
    location.pathname.includes('/new') ||
    location.pathname.includes('/edit')

  // Fermer Sidebar après navigation
  useEffect(() => {
    setMobileOpen(false)
  }, [location.pathname])

  // Fermer avec Escape
  useEffect(() => {
    if (!mobileOpen) return

    const handleKey = (event) => {
      if (event.key === 'Escape') {
        setMobileOpen(false)
      }
    }

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    document.addEventListener('keydown', handleKey)

    return () => {
      document.removeEventListener('keydown', handleKey)
      document.body.style.overflow = previousOverflow
    }
  }, [mobileOpen])

  // Fermer Sidebar sur Desktop
  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 1024px)')

    const handleChange = (event) => {
      if (event.matches) {
        setMobileOpen(false)
      }
    }

    mediaQuery.addEventListener('change', handleChange)

    return () => {
      mediaQuery.removeEventListener('change', handleChange)
    }
  }, [])

  return (
    <div className="flex min-h-screen min-w-0 flex-col overflow-x-hidden bg-graysoft">
      <SidebarContent
        mobileOpen={mobileOpen}
        onClose={() => setMobileOpen(false)}
      />

      {/* MAIN CONTENT */}
      <div className="flex min-h-screen min-w-0 flex-1 flex-col lg:ml-64">
        {/* MOBILE HEADER */}
        <div className="sticky top-0 z-10 flex flex-shrink-0 items-center gap-3 border-b border-gray-100 bg-white px-4 py-3 shadow-sm lg:hidden">
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            className="-ml-2 rounded-xl p-2 text-navy transition-colors hover:bg-graysoft"
            aria-label="Ouvrir le menu"
            aria-expanded={mobileOpen}
          >
            <Menu className="h-6 w-6" />
          </button>

          <span className="text-base font-bold text-navy">
            Tapio
          </span>
        </div>

        {/* TOPBAR */}
        {!isEditorPage && (
          <div className="w-full min-w-0 flex-shrink-0">
            <Topbar />
          </div>
        )}

        {/* PAGE CONTENT */}
        <main
          className={cn(
            'flex min-w-0 flex-1 flex-col',
            isEditorPage
              ? 'p-0'
              : 'p-3 sm:p-4 md:p-5 lg:p-6'
          )}
        >
          <Outlet />
        </main>
      </div>
    </div>
  )
}
